import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Category } from '@/types';
import { categoriesApi } from '@/services/api';

interface CategoriesState {
  categories: Category[];
  categoryTree: Category[];
  slugToIdMap: Record<string, number>;
  currentCategory: Category | null;
  categoryPath: Category[];
  isLoading: boolean;
  isTreeLoading: boolean;
  error: string | null;
}

const initialState: CategoriesState = {
  categories: [],
  categoryTree: [],
  slugToIdMap: {},
  currentCategory: null,
  categoryPath: [],
  isLoading: false,
  isTreeLoading: false,
  error: null,
};

// Async thunks
export const fetchCategories = createAsyncThunk(
  'categories/fetchCategories',
  async (includeAll: boolean = true, { rejectWithValue }) => {
    try {
      const response = await categoriesApi.getAll(includeAll);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to fetch categories');
    }
  }
);

export const fetchCategoryTree = createAsyncThunk(
  'categories/fetchCategoryTree',
  async (_, { rejectWithValue }) => {
    try {
      const response = await categoriesApi.getTree();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to fetch category tree');
    }
  }
);

export const fetchCategoryBySlug = createAsyncThunk(
  'categories/fetchCategoryBySlug',
  async (slug: string, { rejectWithValue }) => {
    try {
      const response = await categoriesApi.getBySlug(slug);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.detail || `Failed to fetch category with slug: ${slug}`);
    }
  }
);

export const fetchCategoryPath = createAsyncThunk(
  'categories/fetchCategoryPath',
  async (categoryId: number, { rejectWithValue }) => {
    try {
      const response = await categoriesApi.getCategoryPath(categoryId);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to fetch category path');
    }
  }
);

// Helper function to build slug-to-id map recursively
const buildSlugToIdMap = (categories: Category[]): Record<string, number> => {
  const map: Record<string, number> = {};

  const processCategory = (category: Category) => {
    map[category.slug] = category.id;

    if (category.children && category.children.length > 0) {
      category.children.forEach(processCategory);
    }
  };

  categories.forEach(processCategory);
  return map;
};

const categoriesSlice = createSlice({
  name: 'categories',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentCategory: (state, action: PayloadAction<Category | null>) => {
      state.currentCategory = action.payload;
    },
    clearCurrentCategory: (state) => {
      state.currentCategory = null;
      state.categoryPath = [];
    },
    setCategoryPath: (state, action: PayloadAction<Category[]>) => {
      state.categoryPath = action.payload;
    },
    clearCategoryPath: (state) => {
      state.categoryPath = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Categories
      .addCase(fetchCategories.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.isLoading = false;
        state.categories = action.payload;
        // Build slug-to-id map
        state.slugToIdMap = buildSlugToIdMap(action.payload);
        state.error = null;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Fetch Category Tree
      .addCase(fetchCategoryTree.pending, (state) => {
        state.isTreeLoading = true;
        state.error = null;
      })
      .addCase(fetchCategoryTree.fulfilled, (state, action) => {
        state.isTreeLoading = false;
        state.categoryTree = action.payload;
        // Build slug-to-id map from tree
        state.slugToIdMap = buildSlugToIdMap(action.payload);
        state.error = null;
      })
      .addCase(fetchCategoryTree.rejected, (state, action) => {
        state.isTreeLoading = false;
        state.error = action.payload as string;
      })

      // Fetch Category by Slug
      .addCase(fetchCategoryBySlug.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCategoryBySlug.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentCategory = action.payload;
        state.error = null;
      })
      .addCase(fetchCategoryBySlug.rejected, (state, action) => {
        state.isLoading = false;
        state.currentCategory = null;
        state.error = action.payload as string;
      })

      // Fetch Category Path
      .addCase(fetchCategoryPath.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCategoryPath.fulfilled, (state, action) => {
        state.isLoading = false;
        state.categoryPath = action.payload;
        state.error = null;
      })
      .addCase(fetchCategoryPath.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  setCurrentCategory,
  clearCurrentCategory,
  setCategoryPath,
  clearCategoryPath,
} = categoriesSlice.actions;

export default categoriesSlice.reducer;
